/**
 * Formats a NANP phone number string as (XXX) XXX-XXXX.
 * Accepts E.164 (+1XXXXXXXXXX), 11 digits with leading 1, or 10 raw digits —
 * with or without formatting characters. Non-NANP / unparseable input is
 * returned unchanged.
 */
export const formatPhoneNumber = (phone: string | null | undefined): string => {
  const trimmedPhone = phone?.trim();
  if (!trimmedPhone) return "";
  const tenDigits = toNANPNationalDigits(trimmedPhone.replace(/\D/g, ""));
  if (tenDigits) {
    return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  }
  return trimmedPhone;
};

export const phoneNumberErrorMessage = (): string => {
  return "Phone number must be a valid North American phone number (e.g., (555) 123-4567)";
};

export const phoneNumberNoCodeLength = () => {
  return 10;
};

// =============================================================================
// NANP +1 country code default
// -----------------------------------------------------------------------------
// All phone input/validation in this app currently assumes a NANP (+1) number.
// PhoneNumber.vue also displays a fixed "+1" InputIcon and stores the model
// value with a leading "1" digit (see the matching block there).
//
// To support other country codes in the future, remove or replace this block
// (along with the matching block in components/PhoneNumber.vue and
// the "NANP" wording in phoneNumberErrorMessage above).
// =============================================================================

export const countryCode = (): string => "1";
const NANP_NATIONAL_DIGITS = 10;

export const toNANPNationalDigits = (digits: string): string | null => {
  if (digits.length === NANP_NATIONAL_DIGITS) return digits;
  if (
    digits.length === NANP_NATIONAL_DIGITS + 1 &&
    digits.startsWith(countryCode())
  ) {
    return digits.slice(1);
  }
  return null;
};

export const phoneNumberLength = (): number => {
  return phoneNumberNoCodeLength() + 1;
};

const NANP_E164_REGEX = new RegExp(
  `^\\+1\\d\{${phoneNumberNoCodeLength()}\}\$`
);

type Digit = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";

export type NANPPhoneNumber = `${"+" | ""}1${Digit}`;
export type PhoneNumberNoCode = `${Digit}`;

export const stripCountryCode = (phone: NANPPhoneNumber): PhoneNumberNoCode => {
  return phone.replace(
    new RegExp("^0?0?\\+?" + countryCode()),
    ""
  ) as PhoneNumberNoCode;
};

export const normalizePhone = (raw: string): NANPPhoneNumber | Error => {
  let normalizedAttempt = raw.trim().replace(/^0+/, "");
  if (normalizedAttempt.length === phoneNumberNoCodeLength()) {
    normalizedAttempt = `+1${normalizedAttempt}`;
  }
  if (normalizedAttempt.length === 11 && normalizedAttempt.startsWith("1")) {
    normalizedAttempt = `+${normalizedAttempt}`;
  }
  return (
    castOnValidPhoneNumber(normalizedAttempt) ??
    new FailedToNormalizePhoneError(phoneNumberErrorMessage())
  );
};

export const isValidPhoneNumber = (
  phone: string | null | undefined
): boolean => {
  if (!phone || !phone.trim()) return false;
  return NANP_E164_REGEX.test(phone);
};

export const castOnValidPhoneNumber = (
  phone: string
): NANPPhoneNumber | null => {
  if (!isValidPhoneNumber(phone)) return null;
  return phone as NANPPhoneNumber;
};

class FailedToNormalizePhoneError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FailedToNormalizePhoneError";
  }
}
